import React from "react";
import { motion } from "framer-motion";
import { CheckCircle, Award, Users, Briefcase } from "lucide-react";
import Navbar from "../../components/Navbar";

// ===== Animations =====
const container = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const slideRight = {
    hidden: { opacity: 0, x: -40 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const slideLeft = {
    hidden: { opacity: 0, x: 40 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const CompanyIntroduction = () => {
    return (
        <>
            <Navbar />

            <section className="bg-white py-16 md:py-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
                <motion.div
                    className="max-w-7xl mx-auto"
                    variants={container}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.2 }}
                >
                    {/* ===== Header ===== */}
                    <motion.div
                        variants={fadeUp}
                        className="text-center mb-12"
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-[var(--bg-primary)]">
                            Who We Are
                        </h2>
                        <div className="w-24 h-1 mx-auto mt-4 mb-6 rounded-full bg-[var(--bg-primary)]"></div>
                        <p className="max-w-3xl mx-auto text-gray-600">
                            MBA Solutions is an ISO-certified consulting and technology solutions company committed to delivering excellence, innovation, and measurable results for businesses worldwide.
                        </p>
                    </motion.div>

                    {/* ===== Main Content ===== */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        {/* Left Content */}
                        <motion.div variants={slideRight}>
                            <h3 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-4">
                                Empowering Businesses Through Standards & Innovation
                            </h3>
                            <p className="text-gray-600 mb-4 leading-relaxed">
                                At MBA Solutions, we specialize in ISO certification, business consultancy, process optimization, and digital transformation. Our mission is to help organizations unlock their true potential by implementing international standards and modern best practices.
                            </p>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                With a team of experienced consultants, auditors, and technical experts, we ensure that your business not only meets compliance requirements but also achieves sustainable growth.
                            </p>

                            <div className="space-y-3">
                                {["ISO Certification & Compliance", "Business Process Optimization", "IT & Digital Solutions", "Training & Consultancy"].map((item, idx) => (
                                    <motion.div
                                        key={idx}
                                        whileHover={{ x: 6 }}
                                        transition={{ type: "spring", stiffness: 200 }}
                                        className="flex items-center gap-3 text-gray-700"
                                    >
                                        <CheckCircle className="text-[var(--bg-primary)]" size={18} />
                                        <span>{item}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Right Cards */}
                        <motion.div variants={slideLeft} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {[{
                                icon: <Award size={26} />,
                                title: "ISO Certified Expertise",
                                desc: "Trusted solutions aligned with international standards."
                            }, {
                                icon: <Users size={26} />,
                                title: "Expert Team",
                                desc: "Qualified professionals with real-world experience."
                            }, {
                                icon: <Briefcase size={26} />,
                                title: "Business Focused",
                                desc: "Strategies designed for growth and efficiency."
                            }, {
                                icon: <CheckCircle size={26} />,
                                title: "Proven Results",
                                desc: "Delivering measurable impact and success."
                            }].map((card, idx) => (
                                <motion.div
                                    key={idx}
                                    whileHover={{ y: -6, scale: 1.03 }}
                                    transition={{ type: "spring", stiffness: 200 }}
                                    className="bg-gray-50 border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:border-[var(--bg-primary)] transition-all"
                                >
                                    <div className="w-12 h-12 mb-4 rounded-full flex items-center justify-center bg-[var(--bg-primary)]/10 text-[var(--bg-primary)]">
                                        {card.icon}
                                    </div>
                                    <h4 className="font-semibold text-lg text-gray-900 mb-2">{card.title}</h4>
                                    <p className="text-gray-600 text-sm">{card.desc}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>

                    {/* ===== CTA ===== */}
                    <motion.div
                        variants={fadeUp}
                        className="mt-16 text-center"
                    >
                        <motion.a
                            href="/contact"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="inline-block px-8 py-3 rounded-full bg-[var(--bg-primary)] text-white font-semibold shadow-md hover:shadow-xl transition-all"
                        >
                            Get Free Consultation
                        </motion.a>
                    </motion.div>
                </motion.div>
            </section>

        </>
    );
};

export default CompanyIntroduction;
